import React, { useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { Button, Card, CardBody, Skeleton, Chip } from '@heroui/react';
import { Plus, Edit, Trash2, Save, FolderOpen, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { FormInput, FormMultiCategorySelector } from './form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import ConfirmationModal from './ConfirmationModal';
import { Department } from '@/services/apis/departments';
import { useCreateDepartment, useUpdateDepartment, useDeleteDepartment } from '@/hooks/queries/useDepartments';

export interface DepartmentFormData {
  name: string;
  categoryIds?: string[];
}

const createDepartmentSchema = (t: (key: string, options?: { defaultValue?: string }) => string) =>
  z.object({
    name: z
      .string()
      .min(1, t('departments.validation.name.required', { defaultValue: 'Name is required' }))
      .max(256, t('departments.validation.name.maxLength', { defaultValue: 'Name must be less than 256 characters' })),
    categoryIds: z.array(z.string().uuid()).optional(),
  });

interface DepartmentManagerProps {
  departments: Department[];
  isLoading?: boolean;
}

const DepartmentManager: React.FC<DepartmentManagerProps> = ({ departments, isLoading = false }) => {
  const { t } = useTranslation('admin');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [departmentToDelete, setDepartmentToDelete] = useState<string | null>(null);

  const createDepartment = useCreateDepartment();
  const updateDepartment = useUpdateDepartment();
  const deleteDepartment = useDeleteDepartment();

  const isSubmitting = createDepartment.isPending || updateDepartment.isPending || deleteDepartment.isPending;

  const departmentSchema = createDepartmentSchema(t);

  const {
    control: addControl,
    handleSubmit: handleAddSubmit,
    reset: resetAddForm,
  } = useForm<DepartmentFormData>({
    resolver: zodResolver(departmentSchema),
    defaultValues: {
      name: '',
      categoryIds: [],
    },
  });

  const {
    control: editControl,
    handleSubmit: handleEditSubmit,
    reset: resetEditForm,
  } = useForm<DepartmentFormData>({
    resolver: zodResolver(departmentSchema),
  });

  const handleAddClick = () => {
    setShowAddForm(true);
    resetAddForm();
  };

  const handleAddFormSubmit: SubmitHandler<DepartmentFormData> = async (data) => {
    try {
      await createDepartment.mutateAsync(data);
      resetAddForm();
      setShowAddForm(false);
    } catch (error) {
      // Error is handled by the mutation's onError callback
    }
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
    resetAddForm();
  };

  const handleEditClick = (department: Department) => {
    setEditingId(department.id);
    resetEditForm({
      name: department.name,
      categoryIds: department.categories?.map((cat) => cat.id) || [],
    });
  };

  const handleEditFormSubmit: SubmitHandler<DepartmentFormData> = async (data) => {
    if (editingId) {
      try {
        await updateDepartment.mutateAsync({ id: editingId, data });
        setEditingId(null);
        resetEditForm();
      } catch (error) {
        // Error is handled by the mutation's onError callback
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    resetEditForm();
  };

  const handleDeleteClick = (id: string) => {
    setDepartmentToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (departmentToDelete) {
      try {
        await deleteDepartment.mutateAsync(departmentToDelete);
        setDeleteModalOpen(false);
        setDepartmentToDelete(null);
      } catch (error) {
        // Error is handled by the mutation's onError callback
      }
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Skeleton className="h-10 w-32 rounded-lg" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">{t('departments.title', { defaultValue: 'Departments' })}</h3>
        {!showAddForm && (
          <Button color="primary" startContent={<Plus size={16} />} onPress={handleAddClick} size="sm">
            {t('departments.addButton', { defaultValue: 'Add Department' })}
          </Button>
        )}
      </div>

      {showAddForm && (
        <Card>
          <CardBody>
            <form onSubmit={handleAddSubmit(handleAddFormSubmit)} className="space-y-4">
              <FormInput
                name="name"
                control={addControl}
                label={t('departments.form.name.label', { defaultValue: 'Department Name' })}
                placeholder={t('departments.form.name.placeholder', { defaultValue: 'Enter department name' })}
                isRequired
              />
              <FormMultiCategorySelector
                name="categoryIds"
                control={addControl}
                label={t('departments.form.categories.label', { defaultValue: 'Categories' })}
                placeholder={t('departments.form.categories.placeholder', { defaultValue: 'Select categories' })}
                parentOnly
              />
              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {t('departments.form.tip', {
                    defaultValue:
                      'Mapping categories to departments helps recommend relevant guides or courses to users in the department.',
                  })}
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="light" onPress={handleCancelAdd} isDisabled={isSubmitting}>
                  {t('departments.actions.cancel', { defaultValue: 'Cancel' })}
                </Button>
                <Button type="submit" color="primary" startContent={<Save size={16} />} isLoading={isSubmitting}>
                  {t('departments.actions.save', { defaultValue: 'Save' })}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      )}

      {departments.length === 0 && !showAddForm ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 mb-4 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center">
            <FolderOpen className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t('departments.empty.title', { defaultValue: 'No departments yet' })}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t('departments.empty.message', { defaultValue: 'Get started by creating your first department' })}
          </p>
          <Button color="primary" startContent={<Plus size={16} />} onPress={handleAddClick} size="sm">
            {t('departments.addButton', { defaultValue: 'Add Department' })}
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {departments.map((department) => (
            <Card key={department.id}>
              <CardBody>
                {editingId === department.id ? (
                  <form onSubmit={handleEditSubmit(handleEditFormSubmit)} className="space-y-4">
                    <FormInput
                      name="name"
                      control={editControl}
                      label={t('departments.form.name.label', { defaultValue: 'Department Name' })}
                      placeholder={t('departments.form.name.placeholder', { defaultValue: 'Enter department name' })}
                      isRequired
                    />
                    <FormMultiCategorySelector
                      name="categoryIds"
                      control={editControl}
                      label={t('departments.form.categories.label', { defaultValue: 'Categories' })}
                      placeholder={t('departments.form.categories.placeholder', { defaultValue: 'Select categories' })}
                      parentOnly
                    />
                    <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                      <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        {t('departments.form.tip', {
                          defaultValue:
                            'Mapping categories to departments helps recommend relevant guides or courses to users in the department.',
                        })}
                      </p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button type="button" variant="light" onPress={handleCancelEdit} isDisabled={isSubmitting}>
                        {t('departments.actions.cancel', { defaultValue: 'Cancel' })}
                      </Button>
                      <Button type="submit" color="primary" startContent={<Save size={16} />} isLoading={isSubmitting}>
                        {t('departments.actions.save', { defaultValue: 'Save' })}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <p className="font-medium mb-2">{department.name}</p>
                      {department.categories && department.categories.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {department.categories.map((category) => (
                            <Chip key={category.id} size="sm" variant="flat" color="primary">
                              {category.name}
                            </Chip>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button isIconOnly variant="light" size="sm" onPress={() => handleEditClick(department)}>
                        <Edit size={16} />
                      </Button>
                      <Button
                        isIconOnly
                        variant="light"
                        color="danger"
                        size="sm"
                        onPress={() => handleDeleteClick(department.id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('departments.delete.title', { defaultValue: 'Delete Department' })}
        message={t('departments.delete.message', {
          defaultValue: 'Are you sure you want to delete this department? This action cannot be undone.',
        })}
        isLoading={deleteDepartment.isPending}
      />
    </div>
  );
};

export default DepartmentManager;
